import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useLocalStorage } from "@mantine/hooks";
import { SendHorizontal, Square, Wand2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import CenterLoader from "../../shared/components/loader/CenterLoader";
import { ChatUpdateType } from "../../shared/enums/ChatEvents";
import { LocalStorageKeys } from "../../shared/enums/LocalStorageKeys";
import { UserRole } from "../../shared/enums/UserRole";
import { useChatUpdates } from "../../shared/hooks/useChatUpdates";
import { useHasRole } from "../../shared/hooks/useHasRole";
import { Chat } from "../../shared/models/Chat";
import { ChatReport } from "../../shared/models/ChatReport";
import { useExportChat } from "./api/useExportChat";
import { useGetChat } from "./api/useGetChat";
import { useGetMessages } from "./api/useGetMessages";
import { useSendUserInput } from "./api/useSendUserInput";
import { useSuggestQuestions } from "./api/useSuggestQuestions";
import MessageList from "./components/MessageList";
import { PromptImproverModal } from "./components/PromptImproverModal";
import { SuggestedQuestions } from "./components/SuggestedQuestions";
import { ChatMessage } from "./models/ChatMessage";
import { ModelOutputMessageData } from "./models/message-data/ModelOutputMessageData";
import { MessageProcessor } from "./models/MessageDataProcessor";
import { Citation, CitationIndex } from "../sources/models/Citation";
import { useStopChat } from "./api/useStopChat";
import { MessageRole } from "./enums/MessageRole";

const wereInternetUsed = (messages: ChatMessage[]): boolean => {
  if (!messages) return false;
  return messages.some((message) => {
    if (!message || !message.data) return false;
    if (message.role === MessageRole.Assistant) {
      const functionCalls = (message.data as ModelOutputMessageData)
        .functionCalls;
      return (
        functionCalls?.some((call) => call.name === "search_internet") || false
      );
    }
    return false;
  });
};

interface ExistingChatProps {
  chatId: string;
  userId?: string;
  autoScroll?: boolean;
  onCitationClick?: (citation: CitationIndex) => void;
  onSourcesClick?: (citations: Citation[]) => void;
}

export default function ExistingChat({
  chatId,
  userId,
  autoScroll = true,
  onCitationClick,
  onSourcesClick,
}: ExistingChatProps) {
  const chatDisabled = useHasRole(UserRole.ChatDisabled);
  const { chat, getChat } = useGetChat(chatId, userId);
  const [inProgress, setInProgress] = useState(chat?.inProgress || false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const { messages: updatedMessages, getMessages } = useGetMessages(chatId);
  const [download, , clearDownload] = useLocalStorage({
    key: LocalStorageKeys.DownloadChatOnFinish,
  });
  const { exportChat } = useExportChat();
  const { newSuggestedQuestions, suggestQuestions } =
    useSuggestQuestions(chatId);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [, setActiveTab] = useState<string>("chat");
  const [, setReport] = useState<ChatReport | undefined>(chat?.report);
  const [, setUseInternetDisabled] = useState(false);
  const lastMessageRef = useRef<ChatMessage | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [input, setInput] = useState("");
  const [showPromptImprover, setShowPromptImprover] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { stopChat } = useStopChat(chatId);

  const setChatProps = function (chat?: Chat) {
    setInProgress(chat?.inProgress || false);
    setSuggestedQuestions(chat?.suggestedQuestions || []);
    setMessages(chat?.messages?.filter((msg) => msg !== undefined) || []);
    if (chat?.finishedAt && download === chatId) {
      clearDownload();
    }
  };

  useEffect(() => {
    // Clear all the states before fetching the chat
    setChatProps();
    getChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  useEffect(() => {
    if (!chat) return;
    setChatProps(chat);

    setReport(chat.report);
    setActiveTab("chat");

    // Scroll to bottom instantly when chat is first loaded
    if (chat.messages && chat.messages.length > 0) {
      scrollToBottom(false);
    }

    if (!chat.finishedAt) return;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat]);

  useEffect(() => {
    if (!updatedMessages) return;
    setMessages(updatedMessages.filter((msg) => msg !== undefined));
    // Scroll to bottom when messages are first loaded
    if (isInitialLoad) {
      scrollToBottom();
      setIsInitialLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedMessages, isInitialLoad]);

  useEffect(() => {
    if (
      !messages ||
      !Array.isArray(messages) ||
      messages.length === 0 ||
      suggestedQuestions.length !== 0
    )
      return;

    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || !lastMessage.data) return;

    // Skip if we've already processed this message
    if (lastMessageRef.current?.id === lastMessage.id) return;

    // Skip if chat is in progress
    if (inProgress) return;

    // Only proceed if we have a valid message with data
    if (lastMessage.role === MessageRole.Assistant) {
      lastMessageRef.current = lastMessage;
      suggestQuestions();
    }
  }, [messages, suggestQuestions, inProgress, suggestedQuestions]);

  useEffect(() => {
    if (newSuggestedQuestions?.length) {
      setSuggestedQuestions(newSuggestedQuestions);
    }
  }, [newSuggestedQuestions]);

  useEffect(() => {
    if (!messages || !Array.isArray(messages)) {
      setUseInternetDisabled(false);
      return;
    }
    setUseInternetDisabled(wereInternetUsed(messages));
  }, [messages]);

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } =
        scrollContainerRef.current;
      const isBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 10;
      setIsAtBottom(isBottom);
    };

    const scrollElement = scrollContainerRef.current;
    scrollElement?.addEventListener("scroll", handleScroll);
    return () => scrollElement?.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!chat) return;

    if (!inProgress && !chat.finishedAt && download === chatId) {
      exportChat(chatId);
      clearDownload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress]);

  useEffect(() => {
    // Only scroll when messages are being streamed, user is at bottom, and autoScroll is true
    if (inProgress && isAtBottom && autoScroll) {
      scrollToBottom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, inProgress, isAtBottom, autoScroll]);

  const scrollToBottom = (smooth = true) => {
    if (!autoScroll) return;

    setTimeout(() => {
      bottomRef.current?.scrollIntoView({
        behavior: smooth ? "smooth" : "instant",
      });
    }, 100); // Small delay to ensure content is rendered
  };

  const processUserInput = async (input: string) => {
    if (!input.trim() || chatDisabled) {
      return;
    }

    setInProgress(true);
    setInput(""); // Clear input immediately after validation
    setSuggestedQuestions([]);
    lastMessageRef.current = null;
    await sendUserInputV2(input);
    // Only scroll when autoScroll is true
    if (autoScroll) {
      scrollToBottom();
    }
  };

  const { sendUserInputV2 } = useSendUserInput(chatId);

  useChatUpdates(chatId, async (type, data) => {
    switch (type) {
      case ChatUpdateType.ChatStarted:
        setInProgress(true);
        break;

      case ChatUpdateType.ChatFinished:
        setInProgress(false);
        break;

      case ChatUpdateType.ChatMessageStarted:
      case ChatUpdateType.ChatMessageFailed:
      case ChatUpdateType.ChatMessageCompleted:
      case ChatUpdateType.ChatMessageCancelled:
        // Instead of fetching all messages, update just the modified message
        // This fixes the N+1 query problem by avoiding a full refetch when only one message changes
        if (data && data.message) {
          setMessages((prev) => {
            // Create a copy of the current messages array
            const updatedMessages = [...prev];
            const messageIndex = updatedMessages.findIndex(
              (msg) => msg.id === data.id,
            );

            // Process the message through MessageProcessor to ensure consistency
            // with how messages are normally handled
            const processedMessage = new ChatMessage(data.message);

            if (messageIndex !== -1) {
              // Update existing message
              updatedMessages[messageIndex] = processedMessage;
            } else {
              // If message doesn't exist yet, add it to the array
              updatedMessages.push(processedMessage);
            }

            // Maintain consistent sorting with the original implementation
            const sortedMessages = updatedMessages.sort(
              (a, b) => +new Date(a.startedAt) - +new Date(b.startedAt),
            );

            return sortedMessages;
          });
        } else {
          // Fallback to full refresh only if we don't have the message data
          await getMessages();
        }
        break;

      case ChatUpdateType.ChatMessageContentUpdated: {
        const messageId = data.messageId;
        const contentUpdate = data.contentUpdate;
        setMessages((prev) =>
          MessageProcessor.ProcessContentUpdate(prev, messageId, contentUpdate),
        );
        break;
      }
    }
  });

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 relative isolate">
        <div
          className="absolute inset-0 overflow-auto"
          ref={scrollContainerRef}
        >
          <div className="space-y-4 px-2">
            {!chat && <CenterLoader />}
            {chat && (
              <>
                {/* Messages */}
                <div className="space-y-4">
                  {messages.length > 0 && (
                    <>
                      <MessageList
                        messages={messages}
                        chat={chat}
                        onCitationClick={onCitationClick}
                        onSourcesClick={onSourcesClick}
                        autoScroll={autoScroll}
                      />
                      <div ref={bottomRef} className="h-4" />
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {chat?.canContinue && (
        <div className="flex-none bg-background border-t z-10">
          <div className="container py-4">
            {messages &&
              messages.length > 0 &&
              messages[messages.length - 1]?.role === MessageRole.Assistant &&
              suggestedQuestions.length > 0 &&
              !inProgress && (
                <SuggestedQuestions
                  suggestedQuestions={suggestedQuestions}
                  processInput={processUserInput}
                />
              )}
            <div className="flex items-center gap-2">
              <Textarea
                placeholder="Type your message..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    processUserInput(input);
                  }
                }}
                disabled={chatDisabled || inProgress}
                className="flex-1 min-h-[44px] resize-none py-2"
                rows={1}
              />
              <Button
                variant="outline"
                onClick={() => input.trim() && setShowPromptImprover(true)}
                className="gap-2 px-3 hover:bg-gray-50 border-gray-200"
                disabled={!input.trim() || chatDisabled || inProgress}
              >
                <Wand2 className="h-4 w-4 text-gray-600" />
                <span className="text-sm text-gray-600">Improve</span>
              </Button>
              {!inProgress && (
                <Button
                  onClick={() => processUserInput(input)}
                  disabled={!input.trim() || chatDisabled}
                >
                  <SendHorizontal className="h-4 w-4" />
                </Button>
              )}
              {inProgress && (
                <Button onClick={stopChat}>
                  <Square className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {chat?.canContinue === false && (
        <div className="flex-none bg-background border-t z-10">
          <div className="container py-4">
            <p className="text-sm text-gray-500 text-center">
              Chat can no longer be continued. Please start a new chat.
            </p>
          </div>
        </div>
      )}

      <PromptImproverModal
        isOpen={showPromptImprover}
        onClose={() => setShowPromptImprover(false)}
        originalPrompt={input}
        chatId={chatId}
        onAccept={(improvedPrompt) => {
          setInput(improvedPrompt);
          setShowPromptImprover(false);
        }}
      />
    </div>
  );
}
